import { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-velocity";
import "leaflet-velocity/dist/leaflet-velocity.css";
import { api } from "@/lib/api";
import { Globe2, Loader2, RefreshCw, Wind, Waves, Navigation, Thermometer, Search, MapPin, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const COMPASS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
const degToCompass = (d) => (d == null ? "" : COMPASS[Math.round(d / 22.5) % 16]);

// nullschool-style animated global map. Wind (GFS) / ocean currents / waves render
// as flowing particles via leaflet-velocity, with an optional coloured field beneath.
const COLORS = [
  "rgb(36,104,180)", "rgb(60,157,194)", "rgb(128,205,193)", "rgb(151,218,168)",
  "rgb(198,231,181)", "rgb(238,247,217)", "rgb(255,238,159)", "rgb(252,217,125)",
  "rgb(255,182,100)", "rgb(252,150,75)", "rgb(250,112,52)", "rgb(245,64,32)",
  "rgb(237,45,28)", "rgb(220,24,32)", "rgb(180,0,35)",
];
const MODES = [
  { key: "wind", label: "Wind", icon: Wind },
  { key: "currents", label: "Currents", icon: Navigation },
  { key: "waves", label: "Waves", icon: Waves },
];
const LEVELS = [
  { key: "surface", label: "Sfc" }, { key: "850", label: "850" }, { key: "700", label: "700" },
  { key: "500", label: "500" }, { key: "250", label: "250" },
];
// blue (low) -> teal -> green (high) — ocean palette for currents & waves
const OCEAN_SCALE = [[8, 21, 58], [14, 47, 122], [21, 95, 176], [27, 147, 168], [43, 176, 131], [111, 208, 90], [182, 227, 106]];
// cold -> hot temperature palette
const TEMP_SCALE = [[68, 26, 110], [40, 70, 170], [30, 130, 200], [45, 180, 175], [120, 200, 110], [225, 205, 90], [240, 150, 55], [225, 80, 45], [150, 20, 35]];
const TEMP_MIN = -30, TEMP_MAX = 45;

function lerpColor(stops, t) {
  t = Math.max(0, Math.min(0.999, t)) * (stops.length - 1);
  const i = Math.floor(t), f = t - i;
  const a = stops[i], b = stops[i + 1] || stops[i];
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}

// Fill null (land / no-data) cells with the average of non-null neighbours over a few
// passes, so the coarse ocean grid interpolates smoothly (land is covered by the mask).
function fillNulls(data, passes = 4) {
  const h = data[0].header; const nx = h.nx, ny = h.ny;
  let u = data[0].data.slice(), v = data[1].data.slice();
  for (let p = 0; p < passes; p++) {
    const nu = u.slice(), nv = v.slice();
    for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
      const idx = j * nx + i;
      if (u[idx] != null) continue;
      let su = 0, sv = 0, c = 0;
      for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
        const jj = j + dj; if (jj < 0 || jj >= ny) continue;
        const k = jj * nx + ((i + di + nx) % nx);
        if (u[k] != null) { su += u[k]; sv += v[k]; c++; }
      }
      if (c) { nu[idx] = su / c; nv[idx] = sv / c; }
    }
    u = nu; v = nv;
  }
  return [{ header: h, data: u }, { header: data[1].header, data: v }];
}

// Render a smooth coloured raster from a scalar accessor, as a full-world imageOverlay.
function renderField(getVal, nx, ny, dx, dy, vmin, vmax, palette) {
  const W = 720, H = 360;
  const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d");
  const img = ctx.createImageData(W, H); const px = img.data;
  for (let y = 0; y < H; y++) {
    const lat = 90 - (y + 0.5) / H * 180;
    const fj = (90 - lat) / dy; const j0 = Math.min(Math.max(Math.floor(fj), 0), ny - 1); const jf = fj - j0; const j1 = Math.min(j0 + 1, ny - 1);
    for (let x = 0; x < W; x++) {
      const gLon = ((-180 + (x + 0.5) / W * 360) + 360) % 360;
      const fi = gLon / dx; const i0 = Math.floor(fi) % nx; const i1 = (i0 + 1) % nx; const xf = fi - Math.floor(fi);
      const corners = [
        [getVal(j0 * nx + i0), (1 - xf) * (1 - jf)], [getVal(j0 * nx + i1), xf * (1 - jf)],
        [getVal(j1 * nx + i0), (1 - xf) * jf], [getVal(j1 * nx + i1), xf * jf],
      ];
      let sum = 0, wsum = 0;
      for (const [val, w] of corners) if (val != null) { sum += val * w; wsum += w; }
      const idx = (y * W + x) * 4;
      if (wsum === 0) { px[idx + 3] = 0; continue; }
      const [r, g, b] = lerpColor(palette, (sum / wsum - vmin) / (vmax - vmin));
      px[idx] = r; px[idx + 1] = g; px[idx + 2] = b; px[idx + 3] = 235;
    }
  }
  ctx.putImageData(img, 0, 0);
  return L.imageOverlay(cv.toDataURL(), [[-90, -180], [90, 180]], { opacity: 0.9, interactive: false });
}

export default function EarthData() {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const velocityRef = useRef(null);
  const landRef = useRef(null);
  const fieldRef = useRef(null);   // coloured scalar overlay (ocean magnitude or heat)
  const markerRef = useRef(null);
  const [mode, setMode] = useState("wind");
  const [level, setLevel] = useState("surface");
  const [overlay, setOverlay] = useState("none");   // wind overlay: none | heat
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState(null);
  const [point, setPoint] = useState(null);
  const [pointLoading, setPointLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);

  const fetchPoint = useCallback(async (lat, lon) => {
    const map = mapRef.current;
    if (!map) return;
    setPointLoading(true);
    if (markerRef.current) markerRef.current.setLatLng([lat, lon]);
    else markerRef.current = L.circleMarker([lat, lon], { radius: 6, color: "#a3e635", weight: 2, fillColor: "#a3e635", fillOpacity: 0.5, interactive: false }).addTo(map);
    markerRef.current.bringToFront();
    try {
      const r = await api.get("/earth/point", { params: { lat, lon } });
      setPoint(r.data);
    } catch { setPoint({ lat, lon, error: true }); }
    finally { setPointLoading(false); }
  }, []);

  const search = useCallback(async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    try {
      const r = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=1`);
      const j = await r.json();
      const hit = (j.results || [])[0];
      if (!hit) { setSearching(false); return; }
      mapRef.current?.flyTo([hit.latitude, hit.longitude], 5, { duration: 1 });
      await fetchPoint(hit.latitude, hit.longitude);
    } catch { /* ignore */ } finally { setSearching(false); }
  }, [query, fetchPoint]);

  const clearPoint = () => {
    setPoint(null);
    if (markerRef.current) { mapRef.current?.removeLayer(markerRef.current); markerRef.current = null; }
  };

  const loadLandMask = useCallback(async (map) => {
    if (landRef.current) { landRef.current.bringToFront(); return; }
    try {
      const geo = await (await fetch(`${process.env.PUBLIC_URL || ""}/land.geojson`)).json();
      const layer = L.geoJSON(geo, { interactive: false, style: { fillColor: "#0a1327", fillOpacity: 1, color: "rgba(255,255,255,0.55)", weight: 0.7 } }).addTo(map);
      landRef.current = layer; layer.bringToFront();
    } catch { /* cosmetic */ }
  }, []);

  const load = useCallback(async (m, lvl, ov) => {
    const map = mapRef.current;
    if (!map) return;
    setLoading(true); setError("");
    try {
      const r = await api.get("/earth/field", { params: { mode: m, level: lvl } });
      [fieldRef, velocityRef, landRef].forEach((ref) => { if (ref.current) { map.removeLayer(ref.current); ref.current = null; } });
      const ocean = m !== "wind";
      let data = r.data.data;
      if (ocean) data = fillNulls(data);
      const h = data[0].header;

      // coloured field beneath particles
      if (ocean) {
        const u = data[0].data, v = data[1].data;
        const mag = (i) => (u[i] == null || v[i] == null) ? null : Math.sqrt(u[i] * u[i] + v[i] * v[i]);
        fieldRef.current = renderField(mag, h.nx, h.ny, h.dx, h.dy, 0, r.data.max, OCEAN_SCALE).addTo(map);
      } else if (ov === "heat") {
        const t = await api.get("/earth/overlay", { params: { field: "temp" } });
        fieldRef.current = renderField((i) => t.data.values[i], t.data.nx, t.data.ny, t.data.dx, t.data.dy, TEMP_MIN, TEMP_MAX, TEMP_SCALE).addTo(map);
      }

      // animated particles
      const layer = L.velocityLayer({
        displayValues: true,
        displayOptions: {
          velocityType: MODES.find((x) => x.key === m)?.label || "Wind",
          position: "bottomleft", emptyString: "Hover the map for a reading",
          angleConvention: "bearingCW", speedUnit: r.data.unit, directionString: "Direction", speedString: "Speed",
        },
        data,
        minVelocity: 0, maxVelocity: r.data.max,
        velocityScale: m === "wind" ? 0.012 : 0.09,
        particleAge: ocean ? 60 : 90, lineWidth: ocean ? 0.9 : 1.2, particleMultiplier: ocean ? 1 / 130 : 1 / 220,
        colorScale: ocean ? ["rgba(255,255,255,0.7)", "rgba(255,255,255,0.95)"] : (ov === "heat" ? ["rgba(255,255,255,0.55)", "rgba(255,255,255,0.9)"] : COLORS),
      });
      layer.addTo(map);
      velocityRef.current = layer;
      if (ocean) await loadLandMask(map);
      setMeta({ updated: r.data.updated, source: r.data.source, unit: r.data.unit, max: r.data.max });
    } catch (e) {
      setError(e?.response?.status === 503 ? "Live data is temporarily unavailable for this layer — please try again shortly." : "Could not load this layer.");
    } finally { setLoading(false); }
  }, [loadLandMask]);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [-25, 134], zoom: 3, minZoom: 2, maxZoom: 8, worldCopyJump: false, zoomControl: true,
      maxBounds: [[-90, -180], [90, 180]], maxBoundsViscosity: 1.0,
    });
    mapRef.current = map;
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: "&copy; OpenStreetMap &copy; CARTO", subdomains: "abcd", maxZoom: 19, noWrap: true,
    }).addTo(map);
    map.on("click", (e) => fetchPoint(e.latlng.lat, ((e.latlng.lng + 180) % 360 + 360) % 360 - 180));
    load("wind", "surface", "none");
    return () => { map.remove(); mapRef.current = null; velocityRef.current = null; landRef.current = null; fieldRef.current = null; markerRef.current = null; };
  }, [load, fetchPoint]);

  const pickMode = (m) => { setMode(m); load(m, m === "wind" ? level : "surface", overlay); };
  const pickLevel = (lvl) => { setLevel(lvl); load("wind", lvl, overlay); };
  const pickOverlay = (ov) => { setOverlay(ov); load(mode, mode === "wind" ? level : "surface", ov); };

  const heatLegend = mode === "wind" && overlay === "heat";
  const legendLabel = heatLegend ? "Temperature" : mode === "wind" ? "Wind speed" : mode === "currents" ? "Current speed" : "Wave height";
  const legendStops = heatLegend ? TEMP_SCALE.map((c) => `rgb(${c[0]},${c[1]},${c[2]})`) : mode === "wind" ? COLORS : OCEAN_SCALE.map((c) => `rgb(${c[0]},${c[1]},${c[2]})`);
  const legendMax = heatLegend ? `${TEMP_MAX}°C` : meta ? `${meta.max}+ ${meta.unit}` : "";
  const legendMin = heatLegend ? `${TEMP_MIN}°C` : "0";

  return (
    <div className="space-y-4" data-testid="earth-data-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Global Conditions · Live Model Data</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2"><Globe2 className="h-7 w-7 text-lime-500" /> Earth Data</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">Animated global wind, ocean currents and wave conditions with an optional temperature overlay. Drag to pan, scroll to zoom.</p>
        </div>
        <Button variant="outline" onClick={() => load(mode, mode === "wind" ? level : "surface", overlay)} disabled={loading} data-testid="earth-refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Refresh
        </Button>
      </div>

      <div className="relative rounded-2xl overflow-hidden border border-slate-800 shadow-lg bg-slate-950" style={{ height: "72vh" }}>
        <div ref={containerRef} className="absolute inset-0 z-0" data-testid="earth-map" />

        {/* location search */}
        <div className="absolute top-3 right-3 z-[500] flex items-center gap-1.5 rounded-full bg-slate-900/85 backdrop-blur border border-slate-700 pl-3 pr-1.5 py-1" data-testid="earth-search">
          <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Search a place…" data-testid="earth-search-input"
            className="h-7 w-40 bg-transparent border-0 text-slate-100 placeholder:text-slate-500 focus-visible:ring-0 px-0 text-xs" />
          <Button size="sm" className="h-7 px-2.5 bg-lime-400 hover:bg-lime-300 text-slate-900 text-xs" onClick={search} disabled={searching} data-testid="earth-search-btn">
            {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Go"}
          </Button>
        </div>

        {/* click-to-read-out panel */}
        {point && (
          <div className="absolute bottom-3 right-3 z-[500] w-60 rounded-xl bg-slate-900/90 backdrop-blur border border-slate-700 text-slate-200 p-3 text-xs" data-testid="earth-readout">
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className="font-semibold text-slate-100 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-lime-400" /> {point.lat.toFixed(2)}°, {point.lon.toFixed(2)}°</p>
              <button onClick={clearPoint} className="text-slate-400 hover:text-white" data-testid="earth-readout-close"><X className="h-3.5 w-3.5" /></button>
            </div>
            {point.error ? <p className="text-slate-400">No reading available here.</p> : pointLoading ? (
              <p className="text-slate-400 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading…</p>
            ) : (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between"><span className="text-slate-400 flex items-center gap-1.5"><Wind className="h-3.5 w-3.5" /> Wind</span><span className="font-medium">{point.wind_speed != null ? `${point.wind_speed.toFixed(1)} m/s ${degToCompass(point.wind_dir)}` : "—"}</span></div>
                <div className="flex items-center justify-between"><span className="text-slate-400 flex items-center gap-1.5"><Thermometer className="h-3.5 w-3.5" /> Temp</span><span className="font-medium">{point.temp != null ? `${point.temp.toFixed(1)} °C` : "—"}</span></div>
                <div className="flex items-center justify-between"><span className="text-slate-400 flex items-center gap-1.5"><Navigation className="h-3.5 w-3.5" /> Current</span><span className="font-medium">{point.current_speed != null ? `${point.current_speed.toFixed(1)} km/h ${degToCompass(point.current_dir)}` : "—"}</span></div>
                <div className="flex items-center justify-between"><span className="text-slate-400 flex items-center gap-1.5"><Waves className="h-3.5 w-3.5" /> Waves</span><span className="font-medium">{point.wave_height != null ? `${point.wave_height.toFixed(1)} m ${degToCompass(point.wave_dir)}` : "—"}</span></div>
                <p className="text-[10px] text-slate-500 pt-1 border-t border-slate-700 mt-1">Click anywhere · current & wave for ocean only</p>
              </div>
            )}
          </div>
        )}

        <div className="absolute top-3 left-3 z-[500] w-56 rounded-xl bg-slate-900/85 backdrop-blur border border-slate-700 text-slate-200 p-3 space-y-3 text-xs" data-testid="earth-controls">
          <div>
            <p className="uppercase tracking-wider text-slate-400 font-bold mb-1.5">Animate</p>
            <div className="grid grid-cols-3 gap-1">
              {MODES.map((m) => {
                const Icon = m.icon; const active = mode === m.key;
                return (
                  <button key={m.key} onClick={() => pickMode(m.key)} data-testid={`earth-mode-${m.key}`}
                    className={`flex flex-col items-center gap-1 py-1.5 rounded-lg transition-colors ${active ? "bg-lime-400 text-slate-900 font-semibold" : "bg-slate-800 hover:bg-slate-700"}`}>
                    <Icon className="h-3.5 w-3.5" /> {m.label}
                  </button>
                );
              })}
            </div>
          </div>
          {mode === "wind" && (
            <>
              <div data-testid="earth-height-group">
                <p className="uppercase tracking-wider text-slate-400 font-bold mb-1.5">Height <span className="text-slate-500 normal-case">(hPa)</span></p>
                <div className="grid grid-cols-5 gap-1">
                  {LEVELS.map((l) => (
                    <button key={l.key} onClick={() => pickLevel(l.key)} data-testid={`earth-level-${l.key}`}
                      className={`py-1.5 rounded-lg transition-colors ${level === l.key ? "bg-lime-400 text-slate-900 font-semibold" : "bg-slate-800 hover:bg-slate-700"}`}>
                      {l.label}
                    </button>
                  ))}
                </div>
                {level === "250" && <p className="text-[10px] text-lime-300 mt-1.5">Jet-stream level (~10.5 km)</p>}
              </div>
              <div data-testid="earth-overlay-group">
                <p className="uppercase tracking-wider text-slate-400 font-bold mb-1.5">Overlay</p>
                <div className="grid grid-cols-2 gap-1">
                  <button onClick={() => pickOverlay("none")} data-testid="earth-overlay-none"
                    className={`py-1.5 rounded-lg transition-colors ${overlay === "none" ? "bg-lime-400 text-slate-900 font-semibold" : "bg-slate-800 hover:bg-slate-700"}`}>None</button>
                  <button onClick={() => pickOverlay("heat")} data-testid="earth-overlay-heat"
                    className={`py-1.5 rounded-lg flex items-center justify-center gap-1 transition-colors ${overlay === "heat" ? "bg-lime-400 text-slate-900 font-semibold" : "bg-slate-800 hover:bg-slate-700"}`}>
                    <Thermometer className="h-3.5 w-3.5" /> Heat</button>
                </div>
              </div>
            </>
          )}
          {meta && (
            <div className="border-t border-slate-700 pt-2 space-y-1 text-[10px] text-slate-400">
              <p><span className="text-slate-300 font-medium">Source:</span> {meta.source}</p>
              <p><span className="text-slate-300 font-medium">Updated:</span> {new Date(meta.updated).toLocaleString()}</p>
            </div>
          )}
        </div>

        {loading && (
          <div className="absolute inset-0 z-[600] flex flex-col items-center justify-center bg-slate-950/70 text-white pointer-events-none" data-testid="earth-loading">
            <Loader2 className="h-8 w-8 animate-spin text-lime-400" />
            <p className="text-sm mt-3 text-slate-300">Loading live data…</p>
          </div>
        )}
        {error && !loading && (
          <div className="absolute inset-0 z-[600] flex items-center justify-center bg-slate-950/80 text-center px-6" data-testid="earth-error">
            <p className="text-sm text-slate-200 max-w-sm">{error}</p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <span className="font-semibold text-slate-700">{legendLabel}</span>
        <div className="flex items-center gap-2">
          <span>{legendMin}</span>
          <div className="h-3 w-48 rounded-full" style={{ background: `linear-gradient(to right, ${legendStops.join(",")})` }} />
          <span>{legendMax}</span>
        </div>
        <span className="ml-auto">Inspired by earth.nullschool.net · Data via Open-Meteo (GFS / Marine)</span>
      </div>
    </div>
  );
}
